import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to fetch user:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const register = async (email, password, fullName, businessName, referralCode) => {
    try {
      const payload = {
        email,
        password,
        full_name: fullName,
        business_name: businessName
      };
      if (referralCode) payload.referral_code = referralCode;
      const response = await axios.post(`${API}/auth/register`, payload);
      if (response.data.requires_verification) {
        return { success: true, requiresVerification: true, email: response.data.email };
      }
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const verifyOtp = async (email, otp) => {
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, { email, otp });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Verification failed'
      };
    }
  };

  const resendOtp = async (email) => {
    try {
      await axios.post(`${API}/auth/resend-otp`, { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Could not resend code'
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      return {
        success: false,
        error: detail || 'Login failed',
        code: detail === 'EMAIL_NOT_VERIFIED' ? 'EMAIL_NOT_VERIFIED' : undefined
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('onboarding_skipped');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const value = {
    user,
    loading,
    token,
    register,
    verifyOtp,
    resendOtp,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};